import { BarChart2, Home, LogOut, Menu, Package, Settings, ShoppingCart, Store, Tag, Truck, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useStoreConfig from '../hooks/useStoreConfig';
import { AuthService } from '../services/authService';

// Add interface for store data
interface StoreData {
  brandName: string;
  storeUrl: string;
  logoUrl: string | null;
  bannerUrl: string | null;
  isBannerActive: boolean;
  socialLinks: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
}

interface MenuItem {
  name: string;
  icon: any;
  path: string;
  subItems?: {
    name: string;
    path: string;
  }[];
}

const AdminHeader = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('Home');
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error, refetch } = useStoreConfig();
  const storeData = data as StoreData;

  console.log(storeData);

  const headerTitle = isLoading ? (
    <div className="animate-pulse">
      <div className="h-6 w-32 bg-gray-200 rounded"></div>
    </div>
  ) : (
    activeItem === 'Home' ? storeData?.brandName : activeItem
  );

  useEffect(() => {
    const path = location.pathname.replace('/admin/', '') || '';
    const foundItem = menuItems.find((item) => item.path === path);
    setActiveItem(foundItem ? foundItem.name : 'Home');
  }, [location.pathname]);
  
  // Check screen size on mount and when window resizes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  
  const menuItems: MenuItem[] = [
    { name: 'Home', icon: Home, path: '' },
    { name: 'Orders', icon: ShoppingCart, path: 'orders' },
    { name: 'Products', icon: Package, path: 'products' },
    { name: 'Categories', icon: Tag, path: 'categories' },
    { name: 'Customers', icon: Users, path: 'customers' },
    { name: 'Payments', icon: BarChart2, path: 'payments' },
    { name: 'Delivery Charges', icon: Truck, path: 'delivery-charges' },
    {name: 'Store', icon: Store, path: 'store'},
    {name: 'Settings', icon: Settings, path: 'settings'},
  ];
  
  // Bottom navigation items (subset of main menu)
  const bottomNavItems = [
    { name: 'Home', icon: Home, path: '' },
    { name: 'Orders', icon: ShoppingCart, path: 'orders' },
    { name: 'Products', icon: Package, path: 'products' },
  ];
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const handleMenuClick = (itemName: string, path: string) => {
    setActiveItem(itemName);
    navigate(`/admin/${path}`);
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogout = async() =>{
  await  AuthService.logout();
  navigate("/")
  }
  
  return (
    <div className="flex flex-col h-screen">
      {/* Top Navigation Bar - Only visible on small/medium screens */}
      {!isLargeScreen && (
        <header className="bg-white border-b border-gray-200 shadow-sm lg:hidden">
          <div className="flex items-center px-4 py-3">
            <div className="flex items-center">
              <button 
                onClick={toggleSidebar}
                className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="ml-4 font-bold text-xl text-gray-800">{headerTitle}</div>
            </div>
          </div>
        </header>
      )}
      
      {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Always visible on large screens, toggleable on small/medium */}
        <aside 
          className={`bg-white text-gray-800 border-r border-gray-200 w-64 ${
            isLargeScreen ? 'relative' : 'fixed inset-y-0 left-0 transform'
          } ${
            (isLargeScreen || isSidebarOpen) ? 'translate-x-0' : '-translate-x-full'
          } transition-transform duration-300 ease-in-out z-50 h-full`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="font-bold text-xl text-gray-800">{storeData?.brandName}</div>
            {!isLargeScreen && (
              <button 
                onClick={toggleSidebar}
                className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
          
          {/* Sidebar Navigation */}
          <nav className="p-4">
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeItem === item.name;
                const hasSubItems = 'subItems' in item && item.subItems !== undefined;
                
                return (
                  <li key={item.name}>
                    <button
                      onClick={() => handleMenuClick(item.name, item.path)}
                      className={`flex items-center gap-3 w-full text-left px-3 py-2 rounded-md ${
                        isActive 
                          ? 'bg-gray-100 text-blue-600 font-medium' 
                          : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : ''}`} />
                      <span>{item.name}</span>
                    </button>
                    {hasSubItems && isActive && item.subItems && (
                      <ul className="mt-1 ml-8 space-y-1">
                        {item.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <button
                              onClick={() => handleMenuClick(subItem.name, subItem.path)}
                              className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm rounded-md text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                            >
                              <span>{subItem.name}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            
            <div className="mt-8 pt-4 border-t border-gray-200">
              <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-50 hover:text-blue-600 w-full text-left">
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </nav>
        </aside>
        
        {/* Main Content */}
        <main className={`flex-1 overflow-y-auto bg-gray-50 ${!isLargeScreen ? 'pb-16' : 'pb-4'}`}>
          {isLargeScreen && (
            <div className="py-4 px-6">
              <h1 className="text-2xl font-semibold text-gray-900">{headerTitle}</h1>
            </div>
          )}

          <Outlet/>
        </main>
      </div>
      
      {/* Bottom Navigation for Mobile */}
      {!isLargeScreen && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
          <div className="flex justify-around items-center h-16">
            {bottomNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem === item.name;
              
              return (
                <button
                  key={item.name}
                  onClick={() => handleMenuClick(item.name, item.path)}
                  className={`flex flex-col items-center justify-center flex-1 h-full ${
                    isActive ? 'text-blue-600' : 'text-gray-600'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                  <span className={`text-xs mt-1 ${isActive ? 'font-medium' : ''}`}>{item.name}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
      
      {/* Overlay for mobile when sidebar is open */}
      {!isLargeScreen && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};

export default AdminHeader;